import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/hooks/use-auth';
import { capturePageView } from '@/lib/posthog';

// Layouts (small, structural — keep eager)
import { AuthLayout } from '@/components/layouts/auth-layout';
import { DashboardLayout } from '@/components/layouts/dashboard-layout';
import { EventLayout } from '@/components/layouts/event-layout';
import { AdminLayout } from '@/components/layouts/admin-layout';

// Guards (small — keep eager)
import { ProtectedRoute } from '@/components/auth/protected-route';
import { GuestRoute } from '@/components/auth/guest-route';
import { AdminRoute } from '@/components/auth/admin-route';
import { CookieConsentProvider } from '@/components/cookie-consent/cookie-consent-provider';
import { ImpersonationBanner } from '@/components/impersonation-banner';

// -- Lazy-loaded pages --

// Auth
const LoginPage = lazy(() => import('@/pages/auth/login').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/register').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/forgot-password').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/reset-password').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('@/pages/auth/verify-email').then(m => ({ default: m.VerifyEmailPage })));
const AuthCallbackPage = lazy(() => import('@/pages/auth/callback').then(m => ({ default: m.AuthCallbackPage })));
const SetPasswordPage = lazy(() => import('@/pages/auth/set-password').then(m => ({ default: m.SetPasswordPage })));

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const EventsPage = lazy(() => import('@/pages/events').then(m => ({ default: m.EventsPage })));
const SubscriptionsPage = lazy(() => import('@/pages/subscriptions').then(m => ({ default: m.SubscriptionsPage })));
const SettingsPage = lazy(() => import('@/pages/settings').then(m => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const NotificationPreferencesPage = lazy(() => import('@/pages/NotificationPreferencesPage').then(m => ({ default: m.NotificationPreferencesPage })));
const BookmarksPage = lazy(() => import('@/pages/bookmarks'));
const InvoicesPage = lazy(() => import('@/pages/invoices'));

// Events
const CreateEventPage = lazy(() => import('@/pages/events/create').then(m => ({ default: m.CreateEventPage })));
const EventDetailPage = lazy(() => import('@/pages/events/detail').then(m => ({ default: m.EventDetailPage })));
const EventInvitationsPage = lazy(() => import('@/pages/events/invitations').then(m => ({ default: m.EventInvitationsPage })));
const EventTemplatesPage = lazy(() => import('@/pages/events/templates').then(m => ({ default: m.EventTemplatesPage })));
const EventDesignPage = lazy(() => import('@/pages/events/design').then(m => ({ default: m.EventDesignPage })));
const EventEditorPage = lazy(() => import('@/pages/events/editor').then(m => ({ default: m.EventEditorPage })));
const EventStatisticsPage = lazy(() => import('@/pages/events/statistics').then(m => ({ default: m.EventStatisticsPage })));
const EventBudgetPage = lazy(() => import('@/pages/events/budget').then(m => ({ default: m.EventBudgetPage })));
const InvitationPreviewPage = lazy(() => import('@/pages/events/preview').then(m => ({ default: m.InvitationPreviewPage })));
const EventAlbumsPage = lazy(() => import('@/pages/events/albums').then(m => ({ default: m.EventAlbumsPage })));
const EventSettingsPage = lazy(() => import('@/pages/events/settings').then(m => ({ default: m.EventSettingsPage })));
const EventSeatingPage = lazy(() => import('@/pages/events/seating').then(m => ({ default: m.EventSeatingPage })));
const EventPackagesPage = lazy(() => import('@/pages/events/packages').then(m => ({ default: m.EventPackagesPage })));
const UploadPortalPage = lazy(() => import('@/pages/events/upload-portal'));
const ModerationPage = lazy(() => import('@/pages/events/moderation'));
const WallPage = lazy(() => import('@/pages/events/wall'));
const EventCheckinPage = lazy(() => import('@/pages/events/checkin').then(m => ({ default: m.EventCheckinPage })));

// Public pages
const InvitationPublicPage = lazy(() => import('@/pages/invitation/public').then(m => ({ default: m.InvitationPublicPage })));
const GuestUploadPage = lazy(() => import('@/pages/upload/portal'));
const LiveWallPage = lazy(() => import('@/pages/live-wall'));
const PublicGalleryPage = lazy(() => import('@/pages/gallery/public'));
const AcceptInvitePage = lazy(() => import('@/pages/invite/accept-invite').then(m => ({ default: m.AcceptInvitePage })));
const CheckinPublicPage = lazy(() => import('@/pages/checkin/public').then(m => ({ default: m.CheckinPublicPage })));
const SelfCheckinPage = lazy(() => import('@/pages/checkin/self').then(m => ({ default: m.SelfCheckinPage })));

// Payment
const PaymentSuccessPage = lazy(() => import('@/pages/payment/success').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import('@/pages/payment/cancel').then(m => ({ default: m.PaymentCancelPage })));

// Wizard & checkout
const TemplatePreviewPage = lazy(() => import('@/pages/templates/preview').then(m => ({ default: m.TemplatePreviewPage })));
const WizardPage = lazy(() => import('@/pages/wizard').then(m => ({ default: m.WizardPage })));
const CheckoutPage = lazy(() => import('@/pages/checkout').then(m => ({ default: m.CheckoutPage })));
const DirectCheckoutRedirect = lazy(() => import('@/pages/checkout/direct-checkout-redirect').then(m => ({ default: m.DirectCheckoutRedirect })));

// Gift
const GiftPurchasePage = lazy(() => import('@/pages/gift').then(m => ({ default: m.GiftPurchasePage })));
const GiftSuccessPage = lazy(() => import('@/pages/gift/success').then(m => ({ default: m.GiftSuccessPage })));
const GiftRedeemPage = lazy(() => import('@/pages/gift/redeem').then(m => ({ default: m.GiftRedeemPage })));
const MyGiftsPage = lazy(() => import('@/pages/gifts').then(m => ({ default: m.MyGiftsPage })));
const SupportPage = lazy(() => import('@/pages/support').then(m => ({ default: m.SupportPage })));

// Marketplace (public)
const MarketplaceIndexPage = lazy(() => import('@/pages/marketplace/index').then(m => ({ default: m.MarketplaceIndexPage })));
const MarketplaceCategoryPage = lazy(() => import('@/pages/marketplace/category').then(m => ({ default: m.MarketplaceCategoryPage })));
const MarketplaceListingPage = lazy(() => import('@/pages/marketplace/listing').then(m => ({ default: m.MarketplaceListingPage })));

// Admin
const AdminDashboardPage = lazy(() => import('@/pages/admin/dashboard'));
const AdminProductsPage = lazy(() => import('@/pages/admin/plans'));
const AdminProductDetailPage = lazy(() => import('@/pages/admin/plan-detail'));
const AdminCreateProductPage = lazy(() => import('@/pages/admin/create-plan'));
const AdminCreateVersionPage = lazy(() => import('@/pages/admin/create-version'));
const AdminSubscriptionsPage = lazy(() => import('@/pages/admin/subscriptions'));
const AdminAuditLogPage = lazy(() => import('@/pages/admin/audit-log'));
const AdminContactPage = lazy(() => import('@/pages/admin/contact'));
const AdminBlogPage = lazy(() => import('@/pages/admin/blog'));
const AdminBlogDashboardPage = lazy(() => import('@/pages/admin/blog-dashboard'));
const AdminBlogEditorPage = lazy(() => import('@/pages/admin/blog-editor'));
const AdminVouchersPage = lazy(() => import('@/pages/admin/vouchers'));
const AdminGiftsPage = lazy(() => import('@/pages/admin/gifts'));
const AdminUsersPage = lazy(() => import('@/pages/admin/users'));
const AdminTemplatesPage = lazy(() => import('@/pages/admin/templates'));
const AdminInvoicesPage = lazy(() => import('@/pages/admin/invoices'));
const AdminBillingSettingsPage = lazy(() => import('@/pages/admin/billing-settings'));
const AdminTicketsPage = lazy(() => import('@/pages/admin/tickets'));
const AdminMarketplacePage = lazy(() => import('@/pages/admin/marketplace'));
const AdminListingsPage = lazy(() => import('@/pages/admin/marketplace/listings'));
const ListingFormPage = lazy(() => import('@/pages/admin/marketplace/listing-form'));
const AdminClaimsPage = lazy(() => import('@/pages/admin/marketplace/claims'));
const AdminCategoriesPage = lazy(() => import('@/pages/admin/marketplace/categories'));

// 404 (small — could be eager, but lazy is fine)
const NotFoundPage = lazy(() => import('@/pages/not-found').then(m => ({ default: m.NotFoundPage })));

function QuestionsRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/events/${id}/editor`} replace />;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin h-8 w-8 border-4 border-stone-300 border-t-stone-600 rounded-full" />
    </div>
  );
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    capturePageView();
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <CookieConsentProvider>
      <ImpersonationBanner />
      <PageViewTracker />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* OAuth callback - always accessible (processes tokens from URL) */}
        <Route path="/app/auth/callback" element={<AuthCallbackPage />} />

        {/* Auth routes (redirect to dashboard if already authenticated) */}
        <Route element={<GuestRoute><AuthLayout /></GuestRoute>}>
          <Route path="/app/login" element={<LoginPage />} />
          <Route path="/app/register" element={<RegisterPage />} />
          <Route path="/app/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/app/reset-password" element={<ResetPasswordPage />} />
          <Route path="/app/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* Set password (for OAuth users) - protected, standalone layout */}
        <Route path="/app/set-password" element={
          <ProtectedRoute>
            <SetPasswordPage />
          </ProtectedRoute>
        } />

        {/* Payment result pages */}
        <Route path="/app/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/app/payment/cancel" element={<PaymentCancelPage />} />

        {/* Public invitation view */}
        <Route path="/i/:code" element={<InvitationPublicPage />} />
        <Route path="/i/preview" element={<InvitationPublicPage isPreview={true} />} />

        {/* Public check-in pages */}
        <Route path="/checkin/:token/self" element={<SelfCheckinPage />} />
        <Route path="/checkin/:token" element={<CheckinPublicPage />} />

        {/* Public upload portal (guest upload via QR) */}
        <Route path="/u/:portalToken" element={<GuestUploadPage />} />

        {/* Public live wall display */}
        <Route path="/live-wall/:accessToken" element={<LiveWallPage />} />

        {/* Public gallery view */}
        <Route path="/g/:accessCode" element={<PublicGalleryPage />} />

        {/* Public template preview (full-screen, no layout) */}
        <Route path="/app/templates/:templateId/preview" element={<TemplatePreviewPage />} />

        {/* Public marketplace (no auth required) */}
        <Route path="/app/furnizori" element={<MarketplaceIndexPage />} />
        <Route path="/app/furnizori/:category" element={<MarketplaceCategoryPage />} />
        <Route path="/app/furnizori/:category/:county" element={<MarketplaceCategoryPage />} />
        <Route path="/app/furnizori/:category/:county/:slug" element={<MarketplaceListingPage />} />

        {/* Public wizard (no auth required until checkout) */}
        <Route path="/wizard/:draftId" element={<WizardPage />} />

        {/* Direct package checkout (creates draft, no auth needed) */}
        <Route path="/checkout/new" element={<DirectCheckoutRedirect />} />

        {/* Checkout (auth required) */}
        <Route path="/checkout/:draftId" element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        } />

        {/* Public collaborator invite acceptance */}
        <Route path="/invite/:token" element={<AcceptInvitePage />} />

        {/* Protected dashboard routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/app/dashboard" element={<DashboardPage />} />
          <Route path="/app/events" element={<EventsPage />} />
          <Route path="/app/events/new" element={<CreateEventPage />} />
          <Route path="/app/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route path="/app/notifications" element={<NotificationsPage />} />
          <Route path="/app/notifications/preferences" element={<NotificationPreferencesPage />} />
          <Route path="/app/bookmarks" element={<BookmarksPage />} />
          <Route path="/app/invoices" element={<InvoicesPage />} />
          <Route path="/app/gifts" element={<MyGiftsPage />} />
          <Route path="/app/support" element={<SupportPage />} />
        </Route>

        {/* Event preview (standalone, no sidebar) */}
        <Route path="/app/events/:id/preview" element={
          <ProtectedRoute>
            <InvitationPreviewPage />
          </ProtectedRoute>
        } />

        {/* Event-specific routes with dedicated layout */}
        <Route
          element={
            <ProtectedRoute>
              <EventLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/app/events/:id" element={<EventDetailPage />} />
          <Route path="/app/events/:id/invitations" element={<EventInvitationsPage />} />
          <Route path="/app/events/:id/seating" element={<EventSeatingPage />} />
          <Route path="/app/events/:id/templates" element={<EventTemplatesPage />} />
          <Route path="/app/events/:id/design" element={<EventDesignPage />} />
          <Route path="/app/events/:id/editor" element={<EventEditorPage />} />
          <Route path="/app/events/:id/statistics" element={<EventStatisticsPage />} />
          <Route path="/app/events/:id/budget" element={<EventBudgetPage />} />
          <Route path="/app/events/:id/questions" element={<QuestionsRedirect />} />
          <Route path="/app/events/:id/albums" element={<EventAlbumsPage />} />
          <Route path="/app/events/:id/upload-portal" element={<UploadPortalPage />} />
          <Route path="/app/events/:id/moderation" element={<ModerationPage />} />
          <Route path="/app/events/:id/wall" element={<WallPage />} />
          <Route path="/app/events/:id/settings" element={<EventSettingsPage />} />
          <Route path="/app/events/:id/packages" element={<EventPackagesPage />} />
          <Route path="/app/events/:id/checkin" element={<EventCheckinPage />} />
        </Route>

        {/* Admin panel routes */}
        <Route
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route path="/app/admin" element={<AdminDashboardPage />} />
          <Route path="/app/admin/products" element={<AdminProductsPage />} />
          <Route path="/app/admin/products/new" element={<AdminCreateProductPage />} />
          <Route path="/app/admin/products/:planId" element={<AdminProductDetailPage />} />
          <Route path="/app/admin/products/:planId/versions/new" element={<AdminCreateVersionPage />} />
          <Route path="/app/admin/subscriptions" element={<AdminSubscriptionsPage />} />
          <Route path="/app/admin/invoices" element={<AdminInvoicesPage />} />
          <Route path="/app/admin/settings/billing" element={<AdminBillingSettingsPage />} />
          <Route path="/app/admin/vouchers" element={<AdminVouchersPage />} />
          <Route path="/app/admin/gifts" element={<AdminGiftsPage />} />
          <Route path="/app/admin/users" element={<AdminUsersPage />} />
          <Route path="/app/admin/templates" element={<AdminTemplatesPage />} />
          <Route path="/app/admin/blog" element={<AdminBlogPage />} />
          <Route path="/app/admin/blog/dashboard" element={<AdminBlogDashboardPage />} />
          <Route path="/app/admin/blog/new" element={<AdminBlogEditorPage />} />
          <Route path="/app/admin/blog/:id/edit" element={<AdminBlogEditorPage />} />
          <Route path="/app/admin/contact" element={<AdminContactPage />} />
          <Route path="/app/admin/tickets" element={<AdminTicketsPage />} />
          <Route path="/app/admin/audit-log" element={<AdminAuditLogPage />} />
          <Route path="/app/admin/marketplace" element={<AdminMarketplacePage />} />
          <Route path="/app/admin/marketplace/listings" element={<AdminListingsPage />} />
          <Route path="/app/admin/marketplace/listings/new" element={<ListingFormPage />} />
          <Route path="/app/admin/marketplace/listings/:id/edit" element={<ListingFormPage />} />
          <Route path="/app/admin/marketplace/claims" element={<AdminClaimsPage />} />
          <Route path="/app/admin/marketplace/categories" element={<AdminCategoriesPage />} />
        </Route>

        {/* Gift redeem (public - no auth required) */}
        <Route path="/gift/redeem/:code" element={<GiftRedeemPage />} />

        {/* Gift purchase (auth required) */}
        <Route path="/gift" element={
          <ProtectedRoute>
            <GiftPurchasePage />
          </ProtectedRoute>
        } />
        <Route path="/gift/success" element={
          <ProtectedRoute>
            <GiftSuccessPage />
          </ProtectedRoute>
        } />

        {/* Root redirect to dashboard */}
        <Route path="/" element={<Navigate to="/app/dashboard" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <Toaster />
      </CookieConsentProvider>
    </AuthProvider>
  );
}

export default App;
